/**
 * SkillDelivery Platform
 *
 * Copyright © 2026 SKD RATWANI Technology & Software Solutions Pvt Ltd
 * All Rights Reserved.
 *
 * SettingsPage -- R13 web Round 1.
 *
 * Owner-area Connect settings: feedbackBookingUrl, feedbackReplyEmail.
 * Both validated server-side; the ack email + reply email pick these
 * up (M10). Admin cannot read or change these per-user values.
 */

import React, { useEffect, useState } from 'react';
import connectApi from '../../services/connectApi';

const SKD_CYAN_DARK = '#0e7490';

export default function SettingsPage() {
  const [state, setState] = useState({ loading: true, data: null });
  const [draft, setDraft] = useState({ feedbackBookingUrl: '', feedbackReplyEmail: '' });
  const [err, setErr] = useState(null);
  const [note, setNote] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const data = await connectApi.getSettings();
        setState({ loading: false, data });
        setDraft({
          feedbackBookingUrl: data.feedbackBookingUrl || '',
          feedbackReplyEmail: data.feedbackReplyEmail || ''
        });
      } catch (e) {
        setErr(e.message || 'Could not load settings.');
        setState({ loading: false, data: {} });
      }
    })();
  }, []);

  const save = async () => {
    setBusy(true); setErr(null); setNote(null);
    try {
      const patch = {
        feedbackBookingUrl: draft.feedbackBookingUrl.trim() || null,
        feedbackReplyEmail: draft.feedbackReplyEmail.trim() || null
      };
      const data = await connectApi.patchSettings(patch);
      setState((s) => ({ ...s, data }));
      setDraft({
        feedbackBookingUrl: data.feedbackBookingUrl || '',
        feedbackReplyEmail: data.feedbackReplyEmail || ''
      });
      setNote('Saved.');
    } catch (e) {
      setErr(e.message || 'Save failed.');
    } finally { setBusy(false); }
  };

  const clear = (key) => setDraft({ ...draft, [key]: '' });

  if (state.loading) return <div style={styles.muted}>Loading settings…</div>;

  return (
    <section>
      <h1 style={styles.h1}>Connect settings</h1>
      <p style={{ color: '#64748b', fontSize: 14, marginTop: 2 }}>
        These settings shape the emails SkillDelivery sends in your name.
        Admin cannot read or change these per-user values.
      </p>

      <div style={styles.card}>
        <label style={styles.field}>
          <span style={styles.fieldLabel}>Booking link (optional)</span>
          <input
            type="url"
            value={draft.feedbackBookingUrl}
            onChange={(e) => setDraft({ ...draft, feedbackBookingUrl: e.target.value })}
            placeholder="https://cal.com/you/intro"
            style={styles.input}
            maxLength={200}
          />
          <small style={styles.helper}>
            When a contact taps "Request a call" on the invite page, the
            acknowledgement email adds a "Pick a slot" button to this
            link. Clear to leave the ack email as plain text.
            {draft.feedbackBookingUrl && (
              <>{' '}<button type="button" onClick={() => clear('feedbackBookingUrl')} style={styles.linkBtn}>Clear</button></>
            )}
          </small>
        </label>

        <label style={styles.field}>
          <span style={styles.fieldLabel}>Reply-to email (optional)</span>
          <input
            type="email"
            value={draft.feedbackReplyEmail}
            onChange={(e) => setDraft({ ...draft, feedbackReplyEmail: e.target.value })}
            placeholder="you@example.co"
            style={styles.input}
            maxLength={240}
          />
          <small style={styles.helper}>
            Used as the <code>Reply-To</code> header on the acknowledgement
            email and your replies. Clear to use your SkillDelivery
            login email.
            {draft.feedbackReplyEmail && (
              <>{' '}<button type="button" onClick={() => clear('feedbackReplyEmail')} style={styles.linkBtn}>Clear</button></>
            )}
          </small>
        </label>

        {err  && <div style={styles.errorBox}>{err}</div>}
        {note && <div style={styles.okBox}>{note}</div>}

        <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
          <button type="button" style={styles.btnPrimary} disabled={busy} onClick={save}>
            {busy ? 'Saving…' : 'Save settings'}
          </button>
        </div>
      </div>
    </section>
  );
}

const styles = {
  h1: { fontSize: 22, color: '#0f172a', margin: 0 },
  card: {
    background: 'white', padding: 16, borderRadius: 10, border: '1px solid #e2e8f0', marginTop: 14
  },
  field: { display: 'block', marginBottom: 14 },
  fieldLabel: {
    display: 'block', fontSize: 12, color: '#64748b', marginBottom: 4,
    textTransform: 'uppercase', letterSpacing: '0.03em'
  },
  helper: { display: 'block', color: '#94a3b8', fontSize: 12, marginTop: 4, lineHeight: 1.5 },
  input: {
    width: '100%', padding: '8px 10px', borderRadius: 8, border: '1px solid #cbd5e1',
    fontSize: 14, fontFamily: 'inherit', background: 'white', boxSizing: 'border-box'
  },
  linkBtn: {
    background: 'transparent', border: 'none', padding: 0, color: SKD_CYAN_DARK,
    textDecoration: 'underline', fontSize: 12, cursor: 'pointer'
  },
  btnPrimary: {
    padding: '8px 16px', borderRadius: 8, background: '#0891b2', color: 'white',
    border: 'none', fontSize: 14, fontWeight: 600, cursor: 'pointer'
  },
  errorBox: {
    padding: 10, background: '#fef2f2', color: '#991b1b',
    borderRadius: 8, border: '1px solid #fecaca', marginTop: 10, fontSize: 13
  },
  okBox: {
    padding: 10, background: '#f0fdf4', color: '#166534',
    borderRadius: 8, border: '1px solid #bbf7d0', marginTop: 10, fontSize: 13
  },
  muted: { color: '#94a3b8', padding: 20, textAlign: 'center' }
};
